import { IS_BROWSER } from "@smartrr/shared/constants";
import React, { useEffect, useState } from "react";
import { Provider as ReduxProvider } from "react-redux";

import { ErrorContainer } from "@customer-app/components/ErrorContainer";
import { PreviewAsCustomerBanner } from "@customer-app/components/PreviewAsCustomerBanner";
import logger from "@customer-app/shared/logger";
import { store } from "@customer-app/state/store";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";
import { OperationAquarius } from "./views/OperationAquarius";
import { ErrorBoundary } from "./views/OperationAquarius/components/ErrorBoundary";
import LDProviderWrapper from "@customer-app/LaunchDarkly/provider";

const allowedDomains = [
  typeof window && window.location.host,
  "https://staging.smartrr.com",
  "https://app.staging.smartrr.com",
  "https://www.smartrr.com",
  "https://smartrr.com",
  "https://app.smartrr.com",
  "https://www.app.smartrr.com",
].filter(Boolean);

if (IS_BROWSER) {
  const reportUnhandled = (event: any | string) => {
    console.trace();
    console.error(event);

    const filename = event.filename || "";

    if (filename !== "" && !allowedDomains.find(domain => filename.startsWith(domain))) {
      return;
    }
    console.error(event.reason, event.message);

    logger.error(event.reason || event.message);
  };

  window.addEventListener("error", reportUnhandled);
  window.addEventListener("unhandledrejection", reportUnhandled);

  const config = {
    release: process.env.RELEASE || "N/A",
  };

  Object.freeze(config);

  Object.defineProperty(window, "smartrr_debug_info", {
    enumerable: false,
    configurable: false,
    writable: false,
    value: config,
  });
}

export default function AppWrapper(): JSX.Element {
  return (
    <ReduxProvider store={store}>
      <App />
    </ReduxProvider>
  );
}

function App() {
  // NOTE: font loading check hangs (never goes to "then"), unsure why
  // const [fontsLoading, setFontsLoading] = useState(false);
  const authIsLoading = useSmartrrCustomerSelector(state => state.auth.isLoading);
  const isLoading =
    useSmartrrCustomerSelector(
      state => state.theme.isLoading || state.purchaseStates.isLoading || state.purchasables.isLoading
    ) || authIsLoading;
  const customerPortalThemeState = useSmartrrCustomerSelector(state => state.theme);
  const customerRelationship = useSmartrrCustomerSelector(state => state.auth.customerRelationship);

  const [, setFontsLoading] = useState(true);

  const authError = !authIsLoading && !customerRelationship;
  useEffect(() => {
    // wait for fonts to load before showing portal to avoid flash of unloaded fonts
    if (customerPortalThemeState.theme) {
      let fontLoadingPromise: Promise<any> = Promise.resolve();
      try {
        // fonts loading API not available in IE
        if (typeof document !== "undefined") {
          const documentFontsPromise = (document as any)?.fonts?.ready;
          if (documentFontsPromise) {
            fontLoadingPromise = documentFontsPromise;
          }
        }
      } finally {
        fontLoadingPromise.then(() => {
          setFontsLoading(false);
        });
      }
    }
  }, [customerPortalThemeState.theme]);

  const contents = () => (
    <React.Fragment>
      {!isLoading && authError ? (
        <AuthError />
      ) : (
        <ErrorBoundary>
          <PreviewAsCustomerBanner {...customerRelationship!} />
          <OperationAquarius />
        </ErrorBoundary>
      )}
    </React.Fragment>
  );

  return <LDProviderWrapper>{contents}</LDProviderWrapper>;
}

function AuthError(): JSX.Element {
  return (
    <ErrorContainer>
      <React.Fragment>
        There was an authentication error, please&nbsp;
        <a href="/account/logout">Log Out</a> &nbsp;and log back in.
      </React.Fragment>
    </ErrorContainer>
  );
}
